import React from 'react';
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * NEXORA Ocean Design System (NODS)
 * Alert Component
 * 
 * Notification alerts with semantic variants
 */

const alertVariants = cva(
  'relative w-full rounded-lg border p-4 transition-opacity duration-200',
  {
    variants: {
      variant: {
        default: 'bg-surface border-border text-text-primary',
        success: 'bg-success/10 border-success/20 text-success',
        warning: 'bg-warning/10 border-warning/20 text-warning',
        error: 'bg-error/10 border-error/20 text-error',
        info: 'bg-primary/10 border-primary/20 text-primary'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
);

const iconMap = {
  default: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
  info: Info
};

export const Alert = React.forwardRef((
  { className, variant = 'default', title, children, onClose, ...props },
  ref
) => {
  const Icon = iconMap[variant];

  return (
    <div
      ref={ref}
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          {title && (
            <h5 className="mb-1 font-medium leading-none tracking-tight">
              {title}
            </h5>
          )}
          <div className="text-sm opacity-90">{children}</div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity"
            aria-label="Close alert"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
});

Alert.displayName = 'Alert';

export default Alert;
